import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SyncComplianceStatusRequest } from '@acs/shared';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useAvailability, useServices } from '../settings/hooks.js';
import { useStaff } from '../staff/hooks.js';
import { businessSetupStatus, type SetupStatus } from './setupChecklist.js';
import { syncComplianceStatus } from '../../shared/api/compliance.js';
import { patchCachedBusiness, patchCachedSourceNumber } from '../../shared/api/businessCache.js';
import { useAuth } from '../auth/index.js';
import {
  beginForwardingTest,
  cancelForwardingTest,
  completeOnboarding,
  declareSourceNumber,
  updateBusinessSettings,
  type BusinessSettingsPatch,
} from '../../shared/api/businesses.js';

/**
 * Onboarding data hooks — the forward-number model (doc 13a).
 *
 * This file used to also wrap the regulated-number KYC flow
 * (`useComplianceRequirements`, `useNumberAvailability`, `useUploadEvidence`,
 * `useExistingEvidence`, `useSubmitCompliance`, `useProvisionNumber`,
 * `useReleaseNumber`). None of them had a caller after onboarding moved to
 * forward numbers, and dead React-Query hooks are worse than no hooks: they
 * read as supported product surface. The typed callable adapters they wrapped
 * (`shared/api/compliance.ts`, `shared/api/provisioning.ts`) are deliberately
 * KEPT — the backend flow is live and Telnyx is about to be exercised against
 * it (doc 17), so rebuilding a UI on top is a matter of re-adding five-line
 * `useMutation` wrappers.
 *
 * `useActivateBusiness` lives in `features/businesses` — it is a business
 * lifecycle action the businesses list uses too, and a second copy here meant
 * two hooks that had to be changed together.
 */

/**
 * Self-heal a missed Twilio approval callback (doc 05 §4.6). Only reachable for
 * a business still at `pending_review`, which the forward-number flow never
 * produces — retained for businesses stranded there before the switch, and for
 * the regulated flow if it is revived.
 */
export function useSyncComplianceStatus() {
  return useMutation({
    mutationFn: (req: SyncComplianceStatusRequest) => syncComplianceStatus(req),
  });
}

/**
 * Record the line the owner will divert to us (doc 18 D2). Mandatory before the
 * forwarding check, because it is what that check compares against.
 *
 * The cache write is the whole reason this is not a bare `useMutation`. The
 * number is printed from the businesses cache, which only a Firestore listener
 * feeds, so without it the owner presses Save and keeps looking at their old
 * number — see `patchCachedSourceNumber`. It runs before the caller's own
 * `onSuccess` (React Query orders mutation callbacks first), which matters
 * because Settings closes the editor in that callback and the card underneath
 * has to already know the new number by then.
 */
export function useDeclareSourceNumber(businessId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (sourceNumber: string) => declareSourceNumber(businessId, sourceNumber),
    onSuccess: (res) =>
      patchCachedSourceNumber(
        qc,
        user?.uid ?? '',
        businessId,
        res.sourceNumber,
        res.reverificationRequired,
      ),
  });
}

/** Open the "confirm the divert landed" window (doc 13a §4.3). */
export function useBeginForwardingTest() {
  return useMutation({ mutationFn: (businessId: string) => beginForwardingTest(businessId) });
}

/**
 * Close the verification window early (doc 18 D2). The declared line is frozen
 * while a check is open, so this is what unlocks it.
 */
export function useCancelForwardingTest() {
  return useMutation({ mutationFn: (businessId: string) => cancelForwardingTest(businessId) });
}

/** Persist the owner's mobile operator — drives the forwarding-fee warning (doc 13a D4). */
export function useSaveRegistration(businessId: string) {
  return useMutation({
    mutationFn: (patch: BusinessSettingsPatch) => updateBusinessSettings(businessId, patch),
  });
}

/**
 * Stamp setup as finished — the flag that releases a business into the app —
 * and open the gate in the same tick.
 *
 * A callable rather than a direct write since doc 18 §7.3: the server refuses
 * while the business cannot answer a call, so an owner cannot leave the wizard
 * with a dashboard that looks live and a phone that silently is not.
 *
 * The optimistic cache write is not a nicety. `ActiveBusinessLayout` decides
 * whether this business may leave the wizard by reading `onboardingCompletedAt`
 * from the businesses cache, and that cache is fed by a Firestore listener. If
 * we navigate on the callable's response alone, the gate is still looking at a
 * business whose setup is unfinished and bounces the owner straight back into
 * the wizard they just completed — losing the destination they picked, because
 * the wizard's own reverse guard then sends them to the dashboard rather than
 * to the test call.
 *
 * Waiting for the snapshot instead does not work either: it re-renders the
 * layout first, which unmounts the wizard before any effect of ours can run.
 * So the write has to land in the cache before navigation, and the server's own
 * timestamp replaces it moments later.
 */
export function useCompleteOnboarding(businessId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: () => completeOnboarding(businessId),
    onSuccess: (res) =>
      patchCachedBusiness(qc, user?.uid ?? '', businessId, {
        onboardingCompletedAt: res.onboardingCompletedAt ?? new Date(),
      }),
  });
}

/**
 * Live setup completeness for one business (see `setupChecklist`).
 *
 * Subscribes to the three collections the checklist reads, through the shared
 * query-key factory — so the shell's banner and the switcher's trigger, which
 * both ask about the active business, read one cache entry per collection
 * rather than each keeping its own copy of the answer.
 *
 * Takes a whole `BusinessDoc` rather than an id because the checklist reads the
 * business's own fields too (greeting, forwarding) — and because the switcher's
 * rows already hold the doc for businesses that are not the active one.
 */
export function useSetupStatus(business: BusinessDoc): SetupStatus {
  const { data: services } = useServices(business.id);
  const { data: availability } = useAvailability(business.id);
  const { data: staff } = useStaff(business.id);
  return businessSetupStatus({ business, services, availability, staff });
}
